/* eslint-disable camelcase */
import { createAction } from 'redux-actions';
import type { Store } from 'redux';
import { HttpClient } from '@piwikpro/http-crate';
import {
  Service,
  inject,
} from '@piwikpro/platform';

export interface TrackerSettingsAttributes {
  updated_at?: string
  urls?: Array<string>
  set_ip_tracking?: boolean
  anonymize_visitor_ip_level?: number | string
  excluded_ips?: Array<string>
  url_query_parameter_to_exclude_from_url?: Array<string>
  excluded_user_agents?: Array<string>
  site_search_query_params?: Array<string>
  site_search_category_params?: Array<string>
  exclude_unknown_urls?: boolean
  keep_url_fragment?: boolean
  anonymize_visitor_geolocation_level?: string
  create_new_visit_when_website_referrer_changes?: boolean
  create_new_visit_when_campaign_changes?: boolean
  trust_visitors_cookies?: boolean
  enable_fingerprinting_across_websites?: boolean
  tracking_fingerprint_disabled?: boolean
  fingerprint_based_on_anonymized_ip?: boolean
  strip_site_search_query_parameters?: boolean
  exclude_crawlers?: boolean
  campaign_name_params?: Array<string>
  campaign_keyword_params?: Array<string>
  campaign_source_params?: Array<string>
  campaign_medium_params?: Array<string>
  campaign_content_params?: Array<string>
  campaign_id_params?: Array<string>
  session_max_event_count?: number
  max_session_duration_seconds?: number
  session_limit_exceeded_action?: 'split_and_exclude' | 'just_split'
  [key: string]: any
}

export interface TrackerSettingsResponse {
  id: string
  type: string
  attributes: TrackerSettingsAttributes
}

export const trackerSettingsFetchInitialized = createAction(
  '@platform/query/trackerSettings/get/init',
);
export const trackerSettingsFetchSucceeded = createAction<
TrackerSettingsAttributes, TrackerSettingsAttributes
>(
  '@platform/query/trackerSettings/get/success',
  (payload: TrackerSettingsAttributes) => payload,
);
export const trackerSettingsFetchFailed = createAction(
  '@platform/query/trackerSettings/get/failed',
);
export const trackerSettingsUpdateInitialized = createAction(
  '@platform/command/trackerSettings/update/init',
);
export const trackerSettingsUpdateSucceeded = createAction<
TrackerSettingsAttributes, TrackerSettingsAttributes
>(
  '@platform/command/trackerSettings/update/success',
  (payload: TrackerSettingsAttributes) => payload,
);
export const trackerSettingsUpdateFailed = createAction(
  '@platform/command/trackerSettings/update/failed',
);
export const trackerSettingsForAppFetchInitialized = createAction(
  '@platform/query/trackerSettingsForApp/get/init',
);
export const trackerSettingsForAppFetchSucceeded = createAction<
TrackerSettingsAttributes, TrackerSettingsAttributes
>(
  '@platform/query/trackerSettingsForApp/get/success',
  (payload: TrackerSettingsAttributes) => payload,
);
export const trackerSettingsForAppFetchFailed = createAction(
  '@platform/query/trackerSettingsForApp/get/failed',
);
export const trackerSettingsForAppUpdateInitialized = createAction(
  '@platform/query/trackerSettingsForApp/update/init',
);
export const trackerSettingsForAppUpdateSucceeded = createAction<
TrackerSettingsAttributes, TrackerSettingsAttributes
>(
  '@platform/query/trackerSettingsForApp/update/success',
  (payload: TrackerSettingsAttributes) => payload,
);
export const trackerSettingsForAppUpdateFailed = createAction(
  '@platform/query/trackerSettingsForApp/update/failed',
);
export const trackerSettingsForAppDeleteInitialized = createAction(
  '@platform/query/trackerSettingsForApp/delete/init',
);
export const trackerSettingsForAppDeleteSucceeded = createAction<
string, string
>(
  '@platform/query/trackerSettingsForApp/delete/success',
  (payload: string) => payload,
);
export const trackerSettingsForAppDeleteFailed = createAction(
  '@platform/query/trackerSettingsForApp/delete/failed',
);

@Service()
export class TrackerSettings {
  private readonly trackerSettingsUrl: string;

  constructor(
    @inject('store') private readonly store: Store,
    @inject('HttpCrate.httpClient') private readonly httpClient: HttpClient,
    @inject('NotificationCrate.interceptors.NotifyOnFail') private readonly notifyOnFailInterceptor: any,
    @inject('NotificationCrate.interceptors.NotifyAboutUpdate') private readonly notifyAboutUpdateInterceptor: any,
    @inject('config') private readonly config: any,
  ) {
    this.trackerSettingsUrl = `${this.config.get('TRACKER_SETTINGS_URL')}/settings/app`;
  }

  async getDetails() : Promise<TrackerSettingsResponse> {
    this.store.dispatch(trackerSettingsFetchInitialized());

    try {
      const response: any = await this.httpClient.request({
        endpoint: `${this.trackerSettingsUrl}/global`,
        method: 'GET',
      })
        .intercept(this.notifyOnFailInterceptor)
        .jsonApi()
        .send();

      this.store.dispatch(trackerSettingsFetchSucceeded(response.data));

      return response;
    } catch (err) {
      this.store.dispatch(trackerSettingsFetchFailed());

      throw err;
    }
  }

  async updateSettings(attributes: TrackerSettingsAttributes): Promise<void> {
    this.store.dispatch(trackerSettingsUpdateInitialized());

    try {
      await this.httpClient.request({
        endpoint: `${this.trackerSettingsUrl}/global`,
        method: 'PATCH',
        body: {
          data: {
            id: 'global',
            type: 'tracker/settings/app/global',
            attributes,
          },
        },
      })
        .intercept(this.notifyAboutUpdateInterceptor)
        .jsonApi()
        .send();

      this.store.dispatch(trackerSettingsUpdateSucceeded(attributes));
    } catch (err) {
      this.store.dispatch(trackerSettingsUpdateFailed());

      throw err;
    }
  }

  async getDetailsForApp(appId: string): Promise<TrackerSettingsResponse> {
    this.store.dispatch(trackerSettingsForAppFetchInitialized());

    try {
      const response: any = await this.httpClient.request({
        endpoint: `${this.trackerSettingsUrl}/${appId}`,
        method: 'GET',
      })
        .intercept(this.notifyOnFailInterceptor)
        .jsonApi()
        .send();

      this.store.dispatch(trackerSettingsForAppFetchSucceeded(response.data));

      return response;
    } catch (err) {
      this.store.dispatch(trackerSettingsForAppFetchFailed());

      throw err;
    }
  }

  async updateSettingsForApp(appId: string, attributes: any, options?: { hideConfirmation?: boolean }): Promise<void | Error> {
    this.store.dispatch(trackerSettingsForAppUpdateInitialized());

    try {
      let request = this.httpClient.request({
        endpoint: `${this.trackerSettingsUrl}/${appId}`,
        method: 'PATCH',
        body: {
          data: {
            id: appId,
            type: 'tracker/settings/app',
            attributes,
          },
        },
      });

      if (!options?.hideConfirmation) {
        request = request.intercept(this.notifyAboutUpdateInterceptor);
      } else {
        request = request.intercept(this.notifyOnFailInterceptor);
      }

      await request.jsonApi().send();

      this.store.dispatch(trackerSettingsForAppUpdateSucceeded(attributes));
    } catch (err) {
      this.store.dispatch(trackerSettingsForAppUpdateFailed());

      throw err;
    }
  }

  async deleteSettingsForApp(appId: string, setting: string): Promise<void | Error> {
    this.store.dispatch(trackerSettingsForAppDeleteInitialized());

    try {
      await this.httpClient.request({
        endpoint: `${this.trackerSettingsUrl}/${appId}/${setting}`,
        method: 'DELETE',
      })
        .intercept(this.notifyAboutUpdateInterceptor)
        .jsonApi()
        .send();

      this.store.dispatch(trackerSettingsForAppDeleteSucceeded(setting));
    } catch (err) {
      this.store.dispatch(trackerSettingsForAppDeleteFailed());

      throw err;
    }
  }
}
